<template>
  <div>
    <loader />

    <div v-if="showComponent" class="print-page">
      <vue-html2pdf
        ref="html2Pdf"
        :show-layout="false"
        :float-layout="false"
        :enable-download="true"
        :preview-modal="false"
        :paginate-elements-by-height="printOptions.paginateElementsByHeight"
        :pdf-quality="1"
        :manual-pagination="printOptions.manualPagination"
        :pdf-content-width="printOptions.contentWidth"
        :html-to-pdf-options="htmlToPdfOptions"
        @has-downloaded="hasDownloaded($event)"
      >
        <template #pdf-content>
          <component :is="currentPrintComponent" @loaded="loaded" />
        </template>
      </vue-html2pdf>
    </div>
  </div>
</template>

<script>
import VueHtml2pdf from '@/modules/vue-html2pdf.vue'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import FinancialOverview from '@/components/generic/presentational/organisms/financialOverview/FinancialOverview.vue'
import VcrPage from '@/pages/vcr/VcrPage.vue'
import QuoteApi from 'api-client/Quote.api'
import pageToPrint from '@/helpers/enums/pageToPrint'
import { mapGetters } from 'vuex'
import { ref } from 'vue'

export default {
  components: {
    FinancialOverview,
    VcrPage,
    VueHtml2pdf,
    Loader,
  },
  setup() {
    const html2Pdf = ref(null)

    return {
      html2Pdf,
    }
  },
  data() {
    return {
      loading: false,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showComponent: false,
      loadTime: null,
      dictionaryTimeout: 1500, // Number of ms to wait for dictionary loading
      printOptions: {
        contentWidth: '100%',
        paginateElementsByHeight: null,
        manualPagination: true,
      },
      htmlToPdfOptions: {
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        margin: 0,
        filename: 'print',
        jsPDF: {
          orientation: 'portrait',
          format: 'a4',
        },
      },
    }
  },
  computed: {
    ...mapGetters({
      quoteId: 'quote/quoteId',
      quoteCar: 'quote/car',
      deliveryDate: 'quote/deliveryDate',
      dictionaryLoaded: 'dictionary/dictionaryLoaded',
    }),
    currentPrintComponent() {
      switch (this.$route.params.page) {
        case pageToPrint.vcrPage:
          return VcrPage

        case pageToPrint.financialOverview:
          return FinancialOverview

        default:
          return null
      }
    },
  },
  created() {
    switch (this.$route.params.page) {
      case pageToPrint.vcrPage:
        this.htmlToPdfOptions.filename = 'vcr-pagina'
        this.htmlToPdfOptions.margin = [5, 0, 5, 0]
        this.printOptions.manualPagination = true
        this.showComponent = true
        break

      case pageToPrint.financialOverview:
        this.htmlToPdfOptions.filename = 'financieel-overzicht'
        this.htmlToPdfOptions.jsPDF.format = 'a1'
        this.htmlToPdfOptions.margin = 10
        this.htmlToPdfOptions.jsPDF.orientation = 'landscape'
        //todo: shared logic w/ CustomerConfigurationPage, thus duplicate code
        QuoteApi.getQuoteById(this.$route.params.quoteId).then(async (data) => {
          this.quote = data
          this.$store.dispatch('quote/loadQuote', this.quote).then(async () => {
            if (this.quoteCar.Type === 'CONFIGURATION') {
              await this.$store.dispatch('configuration/getConfiguration', {
                configurationCode: this.quoteCar.ConfigurationCode,
                deliveryDate: this.deliveryDate,
              })

              await this.$store.dispatch('accessories/getForConfigurationCode', {
                configurationCode: this.quoteCar.ConfigurationCode,
              })

              await this.$store.dispatch(
                'controlCodes/getQuoteOrderControlCodesForConfigurationCode',
                {
                  configurationCode: this.quoteCar.ConfigurationCode,
                  deliveryDate: this.deliveryDate,
                }
              )
            } else if (this.quoteCar.Type === 'STOCKCAR') {
              await this.$store.dispatch('configuration/getStockcar', {
                stockCarId: this.quoteCar.StockCarId,
                configurationId: null,
                type: 'STOCKCAR',
                deliveryDate: this.deliveryDate,
              })

              await this.$store.dispatch('accessories/getForStockCar', {
                stockCarId: this.quoteCar.StockCarId,
              })

              await this.$store.dispatch('controlCodes/getQuoteOrderControlCodesForStockCar', {
                stockCarId: this.quoteCar.StockCarId,
                deliveryDate: this.deliveryDate,
              })
            }
            this.showComponent = true
          })
        })
        break

      default:
        // Print page not valid type
        break
    }
  },
  methods: {
    hasDownloaded() {
      window.close()
    },
    loaded() {
      if (!this.loadTime) {
        this.loadTime = new Date()
      }
      const currentTime = new Date()
      // wait for vcr and dictionary to be loaded, then generate+download pdf
      if (!this.dictionaryLoaded) {
        if (currentTime - this.loadTime >= this.dictionaryTimeout) {
          EventBus.$emit('notification-warning', 'Er is een fout opgetreden...')
          setTimeout(this.hasDownloaded, 5000) // Close window if timeout is reached
        } else {
          setTimeout(this.loaded, 100)
        }
      } else {
        this.html2Pdf.generatePdf()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.print-page {
  display: none;
}

:deep() {
  .button--actions,
  .fixed-bar {
    display: none;
  }

  .vcr-page {
    .o-column {
      padding-left: 1.25rem !important;
      padding-right: 1.25rem !important;
    }
    margin-top: 0;
    padding-left: 0 !important;
    padding-right: 0 !important;
    max-width: 100%;
    font-size: 8pt;

    &__header {
      padding-top: 1rem;
      page-break-inside: avoid;
    }

    &__section {
      page-break-inside: avoid;
    }

    &__table-first-column {
      width: 45%;
    }

    table {
      margin-bottom: 0;
      font-size: 8pt;
    }

    h1 {
      margin-top: 1rem;
      font-size: 26pt;
    }
    h2 {
      font-size: 18pt;
    }
    h3 {
      font-size: 14pt;
    }
    h3 {
      font-size: 12pt;
    }
    table td {
      border-bottom: 0;
      padding: 0.25rem;
    }
    .vcr-configuration-details__option-name {
      display: none;
    }
  }

  .financial-overview {
    &__heading {
      display: block;
      font-size: 2.5em;
    }

    &__title {
      font-size: 2rem;
    }
  }
}
</style>
